import React, { FC, useState, useRef, ChangeEvent, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import Avatar from 'react-avatar'
import lRut from 'rut.js'

import CONFIG from '../../../config'
import { utils } from '../../utils'
import { EDocumentType, INationality, IForecast, IGender, IRegion, ICity, ICommune, FormResponseStatus, AuthUserStatus } from '../../models'
import { userService } from '../../services'
import { useUserNationalities, useForecasts, useUserGenders, useUserRoles, useRegions, useCities, useUserStatus, useCommunes, useUserImages, useUserDocumentsTypes } from '../../hooks'
import { Text, Input, Radio, Button, Checkbox, Select, FormLabel, FormTitle, Icon, FormFieldLoading, FormLabelError, FormFieldset, DatePicker } from '../../components/UI'
import { Modal, useModal } from '../../components/UI/Modal'
import { AdminUserFormDocumentsValidate, RegisterFormDocument } from '..'
import avatarDefaultImg from '../../assets/img/avatar-default.jpg'

type AdminUserFormProps = {
  type?: 'CREATE' | 'EDIT' | 'VALIDATE',
  roleId?: string,
  user?: any,
  onSubmitChange(respStatus: FormResponseStatus, userStatus: AuthUserStatus, message?: string): void
}

interface IUserRegisterForm {
  id: string,
  file?: any,
  documentTypeId: string,
  documentNumber: string,
  firstName: string,
  lastName: string,
  secondName: string,
  dateBirth: any,
  nationalityId: string,
  forecastId: string,
  genderId: string,
  documentFront: any,
  documentBack: any,
  email: string,
  regionId: string,
  cityId: string,
  communeId: string,
  address: string,
  phone: string,
  password: string,
  confirmPassword: string,
  term: boolean,
  personRoleId: string,
  statusId: string
}

const getFieldAdminRequired = (roleId: string | undefined, type: 'CREATE' | 'EDIT' | 'VALIDATE') => {
  return (roleId !== '1' && (type === 'CREATE' || type === 'EDIT'))
}

const getFieldTypeValidateClass = (type: 'CREATE' | 'EDIT' | 'VALIDATE') => {
  return (type === 'VALIDATE' ? 'select--disabled' : '')
}

const AdminUserForm:FC<AdminUserFormProps> = ({ type = 'CREATE', user, roleId, onSubmitChange  })  => {
  const defaultValues: IUserRegisterForm = {
    id: (user && user.id ? user.id : ''),
    file: '',
    documentTypeId: (user ? user.document_type_id : String(EDocumentType.rut)),
    documentNumber: (user ? (user.document_type_id === EDocumentType.rut ? utils.getRutFormat(user.document_number) : user.document_number) : ''),
    firstName: (user ? user.first_name : ''),
    lastName: (user ? user.last_name : ''),
    secondName: (user ? user.second_name : ''),
    dateBirth: (user ? new Date(`${utils.getDateFormat(user.date_birth)}`) : ''),
    nationalityId: (user ? user.nationality_id : ''),
    forecastId: (user ? user.forecast_id : ''),
    genderId: (user ? String(user.gender_id) : ''),
    documentFront: '',
    documentBack: '',
    email: (user ? user.email : ''),
    regionId: (user ? user.region?.region_id : ''),
    cityId: (user ? user.city?.city_id : ''),
    communeId: (user ? user.commune?.commune_id : ''),
    address: (user ? user.address : ''),
    phone: (user ? user.phone : ''),
    password: '',
    confirmPassword: '',
    term: false,
    personRoleId: (user ? String(user.person_role.id) : (roleId ? roleId : '')),
    statusId: (user ? String(user.status_data_id) : '1')
  }

  const { register, handleSubmit, formState: { errors }, watch, setValue, getValues, clearErrors, setError, reset, setFocus, control } = useForm<IUserRegisterForm>({
    defaultValues
  })
  const password = useRef({})
  const [avatarImg, setAvatarImg] = useState<string>('')
  const [submited, setSubmited] = useState<boolean>(false)
  const modal = useModal()

  password.current = watch('password', '')
  const docType = Number(watch('documentTypeId', ''))

  const userRoles = useUserRoles()
  const genders = useUserGenders()
  const nationalities = useUserNationalities()
  const forecasts = useForecasts()
  //console.log('forecasts', forecasts);
  const userStatus = useUserStatus()
  const regions = useRegions()
  const cities = useCities()
  const communes = useCommunes()
  const { userImages } = useUserImages(Number(defaultValues.id))
  const fieldAdminRequired = getFieldAdminRequired(roleId, type)
  const fieldTypeValidateClass = getFieldTypeValidateClass(type)
  const documentsTypes = useUserDocumentsTypes()

  useEffect(() => {
    cities.getCities(Number(defaultValues.regionId))
    communes.getCommunes(Number(defaultValues.cityId))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setForecastId()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forecasts])

  const setForecastId = () => {
    if(defaultValues.forecastId) setValue('forecastId', defaultValues.forecastId)
  }

  const fieldErrorRemove = (field: any, value: string) => {
    setValue(field, value, {
      shouldValidate: true,
      shouldDirty: true
    })
  }

  const postForm = async (data: any) => {
    let resp: any
    let userStatus: AuthUserStatus = ''

    if(type === 'CREATE') resp = await userService.register(data)
    else if(type === 'EDIT') resp = await userService.updateUser(data)
    else if(type === 'VALIDATE') {
      const userId: number = data.get('id')
      userStatus = data.get('userStatus')
      resp = await userService.updateUserStatus(userId, userStatus)
    }
    setSubmited(false)

    if(resp && resp.status === 'Ok') {
      if(type === 'CREATE') reset(defaultValues)
      setTimeout(() => onSubmitChange('SUCCESS', userStatus), 800)
    } else {
      if(type === 'CREATE' || type === 'EDIT') {
        const message = (resp?.statusCode === 400 ? resp?.message : '')
        setTimeout(() => onSubmitChange('ERROR', userStatus, message), 800)

      } else {
        setTimeout(() => onSubmitChange('ERROR', userStatus), 800)
      }
    }
  }

  const onFileChange = (event: any) => {
    const { target: { name, files } } = event

    if(files && files[0]) {
      const fieldName = name
      const fileType = files[0].type
      const fileSize = files[0].size

      if(fileType && (fileType.indexOf('png') === -1 && fileType.indexOf('jpeg') === -1)) {
        setError(fieldName, {
          type: 'format',
          message: 'Selecciona formato png ó jpg'
        })
        return ;
      } else if(fileSize && (fileSize / 1024 > 500)) {
        setError(fieldName, {
          type: 'size',
          message: 'Selecciona tamaño máximo 500KB'
        })
        return ;
      }

      if(name === 'file') {
        const image = URL.createObjectURL(files[0])
        setAvatarImg(image)
      } else {
        setValue(fieldName, files)
      }
      clearErrors(fieldName)
    }
  }

  const onSecondNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { target: { value } } = event
    const fieldName = 'secondName'

    if(value) clearErrors('lastName')

    setValue(fieldName, value)
    setTimeout(() => setFocus(fieldName), 0)
  }

  const onRegionChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const { target: { value } } = event
    cities.getCities(Number(value))
    communes.setEmpty()
    setValue('cityId', '')
    setValue('communeId', '')
    if(value) clearErrors('regionId')
  }

  const onCityChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const { target: { value } } = event
    communes.getCommunes(Number(value))
    setValue('communeId', '')
    if(value) clearErrors('cityId')
  }

  const onPasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { target: { value } } = event
    const fieldName = 'password'

    if(errors.lastName) {
      fieldErrorRemove(fieldName, value)
    } else {
      setValue(fieldName, value)
    }
    setTimeout(() => setFocus(fieldName), 0)
  }

  const onTermChange = (event: any) => {
    const { target: { name, value } } = event

    if(value) clearErrors([name])
  }

  const onSubmit = async (fData: IUserRegisterForm) => {
    if(type === 'CREATE' || type === 'EDIT') {
      const {
        file,
        documentNumber,
        firstName,
        lastName,
        secondName,
        dateBirth,
        nationalityId,
        forecastId,
        genderId,
        email,
        regionId,
        cityId,
        communeId,
        address,
        phone,
        documentFront,
        documentBack,
        personRoleId,
        statusId
      } = fData
      const formReg = new FormData()

      formReg.append('file', (file ? file[0] : ''))
      formReg.append('file_passport', (documentFront ? documentFront[0] : ''))
      formReg.append('file_passport_patient', (documentBack ? documentBack[0] : ''))
      formReg.append('document_type_id', String(docType))
      formReg.append('document_number', (docType === EDocumentType.rut ? lRut.clean(documentNumber) : documentNumber))
      formReg.append('first_name', firstName)
      formReg.append('last_name', lastName)
      formReg.append('second_name', secondName)
      formReg.append('date_birth', `${utils.getDateFormat(dateBirth, 'YYYY-MM-DD')}`)
      formReg.append('nationality_id', nationalityId)
      formReg.append('forecast_id', forecastId)
      formReg.append('gender_id', genderId)
      formReg.append('email', email)
      formReg.append('region_id', regionId)
      formReg.append('city_id', cityId)
      formReg.append('commune_id', communeId)
      formReg.append('address', address)
      formReg.append('phone', phone)
      formReg.append('person_role_id', (personRoleId ? personRoleId : defaultValues.personRoleId))

      if(type === 'EDIT') {
        const { password } = fData

        formReg.append('id', defaultValues.id)
        formReg.append('password', password)
        formReg.append('status_data_id', statusId)
      }

      setSubmited(true)
      postForm(formReg)
    }
  }

  const onStatusValidate = (status: 'ENABLED' | 'DISABLED') => {
    if(type === 'VALIDATE') {
      const formReg = new FormData()

      formReg.append('id', defaultValues.id)
      formReg.append('userStatus', status)
      setSubmited(true)
      postForm(formReg)
    }
  }

  useEffect(() => {
      if (docType === EDocumentType.rut) {
        const formattedRut = lRut.format(watch('documentNumber'), { dots: false })
        setValue('documentNumber', formattedRut)
      }
  }, [watch('documentNumber')]);

  return (
    <>
      <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        <FormTitle>Datos personales</FormTitle>

        <div className="mt-40 lg:mt-50 lg:offset-1">
          <div className="register-form-avatar">
            <Avatar
              src={avatarImg ?
                  avatarImg
                :
                  (userImages && userImages.file_profile ?
                      utils.getImagenBase64(userImages.file_profile)
                    :
                      avatarDefaultImg
                  )
              }
              size="160"
              className="register-form-avatar_img"
            />

            <div className="register-form-avatar_icon">
              <Icon name="photo" />
              <input
                {...register('file')}
                type="file"
                id="file"
                name="file"
                accept="image/x-png,image/jpeg"
                onChange={onFileChange}
              />
            </div>
          </div>
          <Text component="label" size="12" className="mt-5">Imagen con formato .jpg, .png y máximo 500KB</Text>
          {errors.file && <FormLabelError>{errors.file?.message}</FormLabelError>}
        </div>

        <div className="row mt-40">
          <FormFieldset className="lg:order-1">
            <FormLabel htmlFor="firstName" required>Nombres</FormLabel>
            <Input
              {...register('firstName', { required: 'Digita Nombre' })}
              id="firstName"
              name="firstName"
              placeholder="Nombre"
              tabIndex={1}
              fullWidth
              disabled={type === 'VALIDATE'}
              error={!!errors.firstName}
              className="mt-5"
            />
            {errors.firstName && <FormLabelError>{errors.firstName?.message}</FormLabelError>}
          </FormFieldset>

          <FormFieldset className="lg:order-3 mt-30">
            <FormLabel htmlFor="lastName" required>Primer apellido</FormLabel>
            <Input
              {...register('lastName', {
                validate: {
                  required: (value) => {
                    if (!value && !getValues('secondName')) return 'Digita Primer apellido'
                    return true
                  }
                }
              })}
              id="lastName"
              name="lastName"
              placeholder="Apellido"
              tabIndex={2}
              fullWidth
              disabled={type === 'VALIDATE'}
              error={!!errors.lastName}
              className="mt-5"
            />
            {errors.lastName && <FormLabelError>{errors.lastName?.message}</FormLabelError>}
          </FormFieldset>

          <FormFieldset className="lg:order-5 mt-30">
            <FormLabel htmlFor="secondName">Segundo apellido</FormLabel>
            <Input
              {...register('secondName')}
              id="secondName"
              name="secondName"
              placeholder="Apellido"
              tabIndex={3}
              fullWidth
              className="mt-5"
              disabled={type === 'VALIDATE'}
              onChange={onSecondNameChange}
            />
          </FormFieldset>

          <FormFieldset className="lg:order-7 mt-30">
            <FormLabel htmlFor="documentTypeId" required>Tipo de documento</FormLabel>
            {documentsTypes.data && documentsTypes.data.length ?
                <Select
                  {...register('documentTypeId', { required: 'Selecciona Tipo de documento' })}
                  id="documentTypeId"
                  name="documentTypeId"
                  defaultValue={defaultValues.documentTypeId}
                  tabIndex={4}
                  fullWidth
                  required
                  disabled={type === 'VALIDATE'}
                  error={!!errors.documentTypeId}
                  className="mt-5"
                >
                  <option value="" disabled>Selecciona</option>
                  {documentsTypes.data.map((it: any, i: number) => <option key={i} value={it.id}>{it.name}</option>)}
                </Select>
              :
                <Select
                  id="documentTypeIdTmp"
                  name="documentTypeIdTmp"
                  tabIndex={4}
                  fullWidth
                  required
                  loading={true}
                  error={!!errors.documentTypeId}
                  className="mt-5"
                >
                  <option value="" disabled>Selecciona</option>
                </Select>
            }
            {errors.documentTypeId && <FormLabelError>{errors.documentTypeId?.message}</FormLabelError>}
          </FormFieldset>

          <FormFieldset className="lg:order-8 mt-30">
            <FormLabel htmlFor="statusId" required={fieldAdminRequired}>Estado</FormLabel>
            {userStatus.data && userStatus.data.length ?
                <Select
                  {...register('statusId', { required: fieldAdminRequired ? 'Selecciona Estado' : false })}
                  id="statusId"
                  name="statusId"
                  tabIndex={9}
                  fullWidth
                  required
                  disabled={type === 'VALIDATE'}
                  error={!!errors.statusId}
                  className="mt-5"
                >
                  <option value="" disabled>Selecciona</option>
                  {userStatus.data.map((it: any) => <option key={it.id} value={String(it.id)}>{it.name}</option>)}
                </Select>
              :
                <Select
                  id="statusIdTmp"
                  name="statusIdTmp"
                  tabIndex={9}
                  fullWidth
                  required
                  loading={true}
                  error={!!errors.statusId}
                  className="mt-5"
                >
                  <option value="" disabled>Selecciona</option>
                </Select>
            }
            {errors.statusId && <FormLabelError>{errors.statusId?.message}</FormLabelError>}
          </FormFieldset>

          <FormFieldset className="lg:order-9 mt-30">
            <FormLabel htmlFor="documentNumber" required>Número de documento</FormLabel>
            {docType === EDocumentType.rut &&
              <Input
                {...register('documentNumber', {
                  required: `Digita ${CONFIG.AUTH.LABEL.RUT}`,
                  pattern: {
                    value: CONFIG.FORM.PATTERN.RUT.EXP,
                    message: `Digita ${CONFIG.AUTH.LABEL.RUT} válido`
                  },
                  validate: value => lRut.validate(value) || `Digita ${CONFIG.AUTH.LABEL.RUT} válido`
                })}
                id="documentNumber"
                name="documentNumber"
                placeholder={CONFIG.AUTH.PLACEHOLDER.RUT}
                maxLength={CONFIG.FORM.PATTERN.RUT.MAXLENGHT}
                fullWidth
                tabIndex={5}
                disabled={type === 'VALIDATE'}
                error={!!errors.documentNumber}
                className="mt-5"
              />
            }

            {(docType === EDocumentType.passport || (type === 'VALIDATE' && Number(defaultValues.documentTypeId) === EDocumentType.passport)) &&
              <Input
                {...register('documentNumber', {
                  required: `Digita ${CONFIG.AUTH.LABEL.PASSPORT}`,
                  pattern: { value: CONFIG.FORM.PATTERN.PASSPORT.EXP, message: `Digita ${CONFIG.AUTH.LABEL.PASSPORT} válido` },
                  validate: value => true
                })}
                id="documentNumber"
                name="documentNumber"
                placeholder={CONFIG.AUTH.PLACEHOLDER.PASSPORT}
                fullWidth
                disabled={type === 'VALIDATE'}
                error={!!errors.documentNumber}
                className="mt-5"
              />
            }
            {errors.documentNumber && <FormLabelError>{errors.documentNumber?.message}</FormLabelError>}
          </FormFieldset>

          <FormFieldset className="lg:order-10 mt-30">
            <FormLabel htmlFor="personRoleId" required={fieldAdminRequired}>Rol</FormLabel>
            {userRoles.data && userRoles.data.length ?
                <Select
                  {...register('personRoleId', { required: fieldAdminRequired ? 'Selecciona Rol' : false })}
                  id="personRoleId"
                  name="personRoleId"
                  tabIndex={10}
                  fullWidth
                  required
                  disabled={type === 'CREATE' || type === 'VALIDATE'}
                  error={!!errors.personRoleId}
                  className="mt-5"
                >
                  <option value="" disabled>Selecciona</option>
                  {userRoles.data.map((it: any) => <option key={it.id} value={String(it.id)}>{it.name}</option>)}
                </Select>
              :
                <Select
                  id="personRoleIdTmp"
                  name="personRoleIdTmp"
                  tabIndex={10}
                  fullWidth
                  required
                  loading={true}
                  error={!!errors.personRoleId}
                  className="mt-5"
                >
                  <option value="" disabled>Selecciona</option>
                </Select>
            }
            {errors.personRoleId && <FormLabelError>{errors.personRoleId?.message}</FormLabelError>}
          </FormFieldset>

          {(type === 'CREATE' || type === 'VALIDATE' || (type === 'EDIT' && docType === EDocumentType.passport)) &&
            <FormFieldset className="lg:order-11 mt-30">
              <div>
                <FormLabel htmlFor="documentFront">
                  {docType === EDocumentType.rut ? 'Cara frontal de la cédula de identidad' : 'Fotografía del pasaporte'}
                </FormLabel>
                <RegisterFormDocument
                  {...register('documentFront')}
                  id="documentFront"
                  name="documentFront"
                  validate={type === 'VALIDATE'}
                  defaultFile={userImages.file_passport ? utils.getImagenBase64(userImages.file_passport): ''}
                  className="mt-30"
                  onChange={(event) => type === 'VALIDATE' ? modal.open(1)  : onFileChange(event)}
                />
                {errors.documentFront && <FormLabelError>{errors.documentFront?.message}</FormLabelError>}
              </div>

              <div className="mt-30">
                <FormLabel htmlFor="documentBack">
                  {docType === EDocumentType.rut ? 'Cara posterior de la cédula de identidad' : 'Fotografía del usuario con su pasaporte'}
                </FormLabel>
                <RegisterFormDocument
                  {...register('documentBack')}
                  id="documentBack"
                  name="documentBack"
                  docType={docType}
                  side="BACK"
                  defaultFile={userImages.file_passport_patient ? utils.getImagenBase64(userImages.file_passport_patient): ''}
                  validate={type === 'VALIDATE'}
                  className="mt-30"
                  onChange={(event) => type === 'VALIDATE' ? modal.open(2)  : onFileChange(event)}
                />
                {errors.documentBack && <FormLabelError>{errors.documentBack?.message}</FormLabelError>}
              </div>
            </FormFieldset>
          }

          <FormFieldset className="lg:order-2 mt-30 lg:mt-0">
            <FormLabel required={fieldAdminRequired}>Fecha de nacimiento</FormLabel>
            <div className="mt-5">
              <Controller
                control={control}
                name='dateBirth'
                render={({ field }) => (
                  <DatePicker
                    {...register('dateBirth', { required: fieldAdminRequired ? 'Selecciona Fecha de nacimiento' : false })}
                    selected={getValues('dateBirth')}
                    placeholderText="DD/MM/YYYY"
                    tabIndex={6}
                    openToDate={new Date('2000/01/01')}
                    showYearDropdown
                    showMonthDropdown
                    fullWidth
                    error={!!errors.dateBirth}
                    onChange={(date) => field.onChange(date)}
                  />
                )}
              />
            </div>
            {(errors.dateBirth) && <FormLabelError>Selecciona Fecha de nacimiento</FormLabelError>}
          </FormFieldset>

          <FormFieldset className="lg:order-4 mt-30">
            <FormLabel htmlFor="nationalityId" required={fieldAdminRequired}>Nacionalidad</FormLabel>
            {nationalities.data ?
                <Select
                  {...register('nationalityId', { required: fieldAdminRequired ? 'Selecciona Nacionalidad' : false })}
                  id="nationalityId"
                  name="nationalityId"
                  defaultValue=""
                  tabIndex={7}
                  fullWidth
                  required
                  disabled={type === 'VALIDATE'}
                  error={!!errors.nationalityId}
                  className="mt-5"
                >
                  <option value="" disabled>Selecciona</option>
                  {nationalities.data.map((it: INationality) => <option key={it.id} value={it.id}>{it.name}</option>)}
                </Select>
              :
                <Select
                  id="nationalityIdTmp"
                  name="nationalityIdTmp"
                  tabIndex={7}
                  fullWidth
                  required
                  loading={true}
                  error={!!errors.nationalityId}
                  className="mt-5"
                >
                  <option value="" disabled>Selecciona</option>
                </Select>
            }
            {errors.nationalityId && <FormLabelError>{errors.nationalityId?.message}</FormLabelError>}
          </FormFieldset>

          <FormFieldset className="lg:order-6 mt-30">
            <FormLabel htmlFor="forecastId" required={fieldAdminRequired}>Previsión</FormLabel>
            {forecasts.data ?
                <Select
                  {...register('forecastId', { required: fieldAdminRequired ? 'Selecciona Previsión' : false })}
                  id="forecastId"
                  name="forecastId"
                  defaultValue=""
                  tabIndex={8}
                  fullWidth
                  required
                  disabled={type === 'VALIDATE'}
                  error={!!errors.forecastId}
                  className="mt-5"
                >
                  <option value="" disabled>Selecciona</option>
                  {forecasts.data.map((it: IForecast) => <option key={it.id} value={it.id} hidden={it.status == 0 ? true : false}>{it.nombre}</option>)}
                </Select>
              :
                <Select
                  id="forecastIdTmp"
                  name="forecastIdTmp"
                  tabIndex={7}
                  fullWidth
                  required
                  loading={true}
                  error={!!errors.forecastId}
                  className="mt-5"
                >
                  <option value="" disabled>Selecciona</option>
                </Select>
            }
            {errors.forecastId && <FormLabelError>{errors.forecastId?.message}</FormLabelError>}
          </FormFieldset>

          <FormFieldset className="lg:order-12 mt-30">
            <FormLabel required={fieldAdminRequired}>Género</FormLabel>
            <div className="position-relative mt-5 py-5">
              {genders.data ?
                  genders.data.map((it: IGender, i: number) => {
                    return (
                      <div key={it.id} className={`d-inline-block${i > 0 ? ' ml-20' : ''}`}>
                        <Radio
                          {...register('genderId', { required: fieldAdminRequired ? 'Selecciona Género' : false })}
                          id={`sex${it.name}`}
                          name="sex"
                          value={it.id}
                          tabIndex={i + 11}
                          disabled={type === 'VALIDATE'}
                          error={!!errors.genderId}
                          onChange={() => fieldErrorRemove('genderId', String(it.id))}
                        />
                        <Text component="label" htmlFor={`sex${it.name}`} size="14" weight="semibold">{it.name}</Text>
                      </div>
                    )
                  })
                :
                  <FormFieldLoading overlay />
              }
            </div>
            {errors.genderId && <FormLabelError>{errors.genderId?.message}</FormLabelError>}
          </FormFieldset>
        </div>

        <FormTitle className="mt-60">Datos de contacto</FormTitle>

        <div className="row mt-40">
          <FormFieldset className="lg:order-1">
            <FormLabel htmlFor="email" required>E-mail</FormLabel>
            <Input
              {...register('email', { required: 'Digita E-mail', pattern: { value: CONFIG.FORM.PATTERN.EMAIL, message: 'Digita E-mail válido' } })}
              id="email"
              name="email"
              placeholder="E-mail"
              tabIndex={14}
              fullWidth
              disabled={type === 'VALIDATE'}
              error={!!errors.email}
              className="mt-5"
            />
            {errors.email && <FormLabelError>{errors.email?.message}</FormLabelError>}
          </FormFieldset>

          <FormFieldset className="lg:order-3 mt-30">
            <FormLabel htmlFor="address" required={fieldAdminRequired}>Dirección</FormLabel>
            <Input
              {...register('address', { required: fieldAdminRequired ? 'Digita Dirección' : false })}
              id="address"
              name="address"
              placeholder="Calle"
              tabIndex={15}
              fullWidth
              disabled={type === 'VALIDATE'}
              error={!!errors.address}
              className="mt-5"
            />
            {errors.address && <FormLabelError>{errors.address?.message}</FormLabelError>}
          </FormFieldset>

          <FormFieldset className="lg:order-5 mt-30">
            <FormLabel htmlFor="phone" required={fieldAdminRequired}>Teléfono</FormLabel>
            <Input
              {...register('phone', { required: fieldAdminRequired ? 'Digita Teléfono' : false, pattern: { value: CONFIG.FORM.PATTERN.PHONE.EXP, message: 'Digita Teléfono válido' }})}
              type="tel"
              id="phone"
              name="phone"
              placeholder="9XXXXXXXX"
              maxLength={CONFIG.FORM.PATTERN.PHONE.MAXLENGHT}
              tabIndex={16}
              fullWidth
              disabled={type === 'VALIDATE'}
              error={!!errors.phone}
              className="mt-5"
            />
            {errors.phone && <FormLabelError>{errors.phone?.message}</FormLabelError>}
          </FormFieldset>

          <FormFieldset className="lg:order-2 mt-30 lg:mt-0">
            <FormLabel htmlFor="regionId" required={fieldAdminRequired}>Región</FormLabel>
            {regions.data && regions.data.length ?
                <>
                  {type === 'VALIDATE' && <div className="field-disabled"></div>}
                  <Select
                    {...register('regionId', { required: fieldAdminRequired ? 'Selecciona Región' : false })}
                    id="regionId"
                    name="regionId"
                    tabIndex={17}
                    fullWidth
                    required
                    error={!!errors.regionId}
                    className={`mt-5 ${fieldTypeValidateClass}`}
                    onChange={onRegionChange}
                  >
                    <option value="" disabled>Selecciona</option>
                    {regions.data.map((it: IRegion) => <option key={it.id} value={it.id}>{it.name}</option>)}
                  </Select>
                </>
              :
                <Select
                  id="regionIdTmp"
                  name="regionIdTmp"
                  tabIndex={17}
                  fullWidth
                  required
                  loading={true}
                  error={!!errors.regionId}
                  className="mt-5"
                >
                  <option value="" disabled>Selecciona</option>
                </Select>
            }
            {errors.regionId && <FormLabelError>{errors.regionId?.message}</FormLabelError>}
          </FormFieldset>

          <FormFieldset className="lg:order-4 mt-30">
            <FormLabel htmlFor="cityId" required={fieldAdminRequired}>Provincia</FormLabel>
            {cities.data && cities.data.length ?
                <>
                  {type === 'VALIDATE' && <div className="field-disabled"></div>}
                  <Select
                    {...register('cityId', { required: fieldAdminRequired ? 'Selecciona Provincia' : false })}
                    id="cityId"
                    name="cityId"
                    defaultValue={defaultValues.cityId}
                    tabIndex={18}
                    fullWidth
                    required
                    error={!!errors.cityId}
                    className={`mt-5 ${fieldTypeValidateClass}`}
                    onChange={onCityChange}
                  >
                    <option value="">Selecciona</option>
                    {cities.data.map((it: ICity) => <option key={it.id} value={it.id}>{it.name}</option>)}
                  </Select>
                </>
              :
                <Select
                  id="cityIdTmp"
                  name="cityIdTmp"
                  defaultValue=""
                  tabIndex={18}
                  fullWidth
                  required
                  error={!!errors.cityId}
                  disabled={!cities.data?.length}
                  loading={cities.data === null}
                  className="mt-5"
                >
                  <option value="">Selecciona</option>
                </Select>
            }
            {errors.cityId && <FormLabelError>{errors.cityId?.message}</FormLabelError>}
          </FormFieldset>

          <FormFieldset className="lg:order-5 mt-30">
            <FormLabel htmlFor="communeId" required={fieldAdminRequired}>Comuna</FormLabel>
            {communes.data && communes.data.length ?
                <>
                  {type === 'VALIDATE' && <div className="field-disabled"></div>}
                  <Select
                    {...register('communeId', { required: fieldAdminRequired ? 'Selecciona Comuna' : false })}
                    id="communeId"
                    name="communeId"
                    defaultValue={defaultValues.communeId}
                    tabIndex={19}
                    fullWidth
                    required
                    error={!!errors.communeId}
                    className={`mt-5 ${fieldTypeValidateClass}`}
                  >
                    <option value="">Selecciona</option>
                    {communes.data.map((it: ICommune) => <option key={it.id} value={it.id}>{it.name}</option>)}
                  </Select>
                </>
              :
                <Select
                  id="communeIdTmp"
                  name="communeIdTmp"
                  defaultValue=""
                  tabIndex={19}
                  fullWidth
                  required
                  error={!!errors.communeId}
                  disabled={true}
                  loading={communes.data === null}
                  className="mt-5"
                >
                  <option value="">Selecciona</option>
                </Select>
            }
            {errors.communeId && <FormLabelError>{errors.communeId?.message}</FormLabelError>}
          </FormFieldset>
        </div>

        {type === 'EDIT' &&
          <>
            <FormTitle className="mt-60">Datos de la cuenta</FormTitle>

            <div className="row mt-40">
              <FormFieldset>
                <FormLabel htmlFor="password">Contraseña</FormLabel>
                <Input
                  {...register('password',
                    {
                      pattern: {
                        value: CONFIG.FORM.PATTERN.PASSWORD.EXP,
                        message: 'Digita Contraseña válida'
                      },
                      minLength: {
                        value: CONFIG.FORM.PATTERN.PASSWORD.MINLENGHT,
                        message: `Digita mínimo ${CONFIG.FORM.PATTERN.PASSWORD.MINLENGHT} caracteres`
                      }
                    }
                  )}
                  type="password"
                  id="password"
                  name="password"
                  placeholder="Contraseña"
                  tabIndex={20}
                  fullWidth
                  autoComplete="new-password"
                  error={!!errors.password}
                  className="mt-5"
                  onChange={onPasswordChange}
                />
                {errors.password && <FormLabelError>{errors.password?.message}</FormLabelError>}
                <Text component="label" size="11" className="lg:fz-12 mt-10 lg:ml-20">Mínimo: 8 caracteres, 1 máyúscula, 1 minúscula y 1 número.</Text>
              </FormFieldset>

              <FormFieldset className="mt-30 lg:mt-0">
                <FormLabel htmlFor="confirmPassword">Confirmación de contraseña</FormLabel>

                <Input
                  {...register('confirmPassword',
                    {
                      pattern: { value: CONFIG.FORM.PATTERN.PASSWORD.EXP,
                        message: 'Digita Contraseña válida'
                      },
                      minLength: {
                        value: CONFIG.FORM.PATTERN.PASSWORD.MINLENGHT,
                        message: `Digita mínimo ${CONFIG.FORM.PATTERN.PASSWORD.MINLENGHT} caracteres`
                      },
                      validate: value => value === password.current || 'Las contraseñas no coinciden'
                    }
                  )}
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  placeholder="Confirmación de contraseña"
                  tabIndex={21}
                  fullWidth
                  error={!!errors.confirmPassword}
                  className="mt-5"
                />
                {errors.confirmPassword && <FormLabelError>{errors.confirmPassword?.message}</FormLabelError>}
              </FormFieldset>
            </div>
          </>
        }

        {type === 'CREATE' &&
          <>
            <FormTitle className="mt-60">Términos y condiciones</FormTitle>

            <div className="row mt-40">
              <fieldset className="col-12 lg:text-center">
                <div>
                  <Checkbox
                    {...register('term', { required: fieldAdminRequired ? 'Acepta términos y condiciones' : false })}
                    id="term"
                    name="term"
                    value="true"
                    tabIndex={22}
                    error={!!errors.term}
                    onChange={onTermChange}
                  />
                  <FormLabel htmlFor="term" required={fieldAdminRequired}>
                    <a href="https://www.indisa.cl/files/2021/08/PORTAL_Terminos-y-condiciones.pdf" target="_blank" rel="noreferrer" className="text-du">Aceptar términos y condiciones</a>
                  </FormLabel>
                </div>
                {errors.term && <FormLabelError>{errors.term?.message}</FormLabelError>}
              </fieldset>
            </div>
          </>
        }

        <FormTitle className="mt-60" required>Campo obligatorio</FormTitle>

        {type === 'VALIDATE' ?
            <div className="register-form-btn-wrapper-2 mt-50">
              <div className="row">
                <div className="col-6 pr-10">
                  <div className="position-relative">
                    <Button
                      tabIndex={22}
                      fullWidth
                      disabled={submited}
                      loading={submited}
                      onClick={() => onStatusValidate('DISABLED')}
                    >DENEGAR</Button>
                  </div>
                </div>

                <div className="col-6 pl-10">
                  <div className="position-relative">
                    <Button
                      tabIndex={23}
                      fullWidth
                      disabled={submited}
                      loading={submited}
                      onClick={() => onStatusValidate('ENABLED')}
                    >CONFIRMAR</Button>
                  </div>
                </div>
              </div>
            </div>
          :
            <div className="register-form-btn-wrapper mt-50 lg:mt-60">
              <Button
                type="submit"
                tabIndex={22}
                fullWidth
                disabled={submited}
                loading={submited}
              >{type === 'CREATE' ? 'CREAR' : 'ACTUALIZAR'}</Button>
            </div>
        }
      </form>

      <Modal show={modal.show} maxWidth="950px" onClose={() => modal.close()}>
        <AdminUserFormDocumentsValidate images={userImages} indexSelected={modal.index} />
      </Modal>
    </>
  )
}

export { AdminUserForm }